import React, { useState, useEffect, useRef } from 'react';
import { 
  saveDraftInvoice, 
  saveDraftProforma, 
  generateLiveInvoice, 
  listInvoices, 
  deleteDraftInvoice, 
  getDraftInvoice, 
  updateDraftInvoice 
} from '../../lib/services/billingService';
import { billingService } from '../../services/billingService';
import { testInvoiceService, generateTestDocument as generateTestInvoiceData, generateTestDocumentFromDraft } from '../../services/testInvoiceService';
import { InvoiceRequest, ProformaRequest, InvoiceListItem } from '../../types/billing';
import { addDoc, collection, getDoc, doc, getDocs, query, where, limit } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import InvoiceForm from './InvoiceForm';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';
import Modal from '../common/Modal';
import Pagination from '../common/Pagination';
import { useAuth } from '../../contexts/AuthContext';
import { 
  PlusIcon, 
  DocumentTextIcon, 
  MagnifyingGlassIcon,
  CalendarIcon,
  BanknotesIcon,
  EyeIcon,
  CheckCircleIcon,
  ClockIcon,
  CogIcon,
  DocumentIcon,
  PencilIcon,
  TrashIcon,
  ChevronDownIcon,
  ReceiptPercentIcon,
  XCircleIcon,
  CurrencyDollarIcon,
  LockClosedIcon,
  ShieldCheckIcon
  } from '@heroicons/react/24/outline';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

interface InvoiceManagerProps {
  onCreateInvoice?: () => void;
}

const InvoiceManager: React.FC<InvoiceManagerProps> = ({ onCreateInvoice }) => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState<InvoiceListItem[]>([]);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [formType, setFormType] = useState<'INVOICE' | 'PROFORMA'>('INVOICE');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<'all' | 'paid' | 'unpaid' | 'overdue' | 'draft' | 'dev'>('all');
  const [editingInvoice, setEditingInvoice] = useState<any>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState<string | null>(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [invoiceToGenerate, setInvoiceToGenerate] = useState<string | null>(null);
  const [generateType, setGenerateType] = useState<'live' | 'dev'>('live');
  
  // Warranty modal states
  const [showWarrantyModal, setShowWarrantyModal] = useState(false);
  const [invoiceToWarranty, setInvoiceToWarranty] = useState<InvoiceListItem | null>(null);
  
  // Payment status update state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [invoiceToUpdate, setInvoiceToUpdate] = useState<InvoiceListItem | null>(null);
  const [paymentDate, setPaymentDate] = useState<string>('');
  
  // Credit note state
  const [showCreditNoteModal, setShowCreditNoteModal] = useState(false);
  const [invoiceToCredit, setInvoiceToCredit] = useState<InvoiceListItem | null>(null);
  const [creditNoteReason, setCreditNoteReason] = useState<string>('');
  const [creditNoteType, setCreditNoteType] = useState<'FULL' | 'PARTIAL'>('FULL');
  
  // Developer mode state
  const [isDeveloperMode, setIsDeveloperMode] = useState(false);
  
  // PIN modal state
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const pinInputRef = useRef<HTMLInputElement>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    if (user?.organizationId) {
      loadInvoices();
    }
  }, [user?.organizationId]);

  // Dropdown bezárása kattintáskor máshova
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showDropdown) {
        setShowDropdown(false);
      }

    };

    if (showDropdown) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showDropdown]);

  // External trigger for creating invoice
  useEffect(() => {
    if (onCreateInvoice) {
      // Make the function available externally
      (window as any).triggerCreateInvoice = (type: 'INVOICE' | 'PROFORMA') => {
        openInvoiceForm(type);
      };
    }
    return () => {
      delete (window as any).triggerCreateInvoice;
    };
  }, [onCreateInvoice]);

  // Developer mode keyboard shortcut (Ctrl + Shift + D)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'D') {
        event.preventDefault();
        
        if (isDeveloperMode) {
          // Ha már be van kapcsolva, kikapcsoljuk PIN nélkül
          setIsDeveloperMode(false);
          toast('Fejlesztői mód kikapcsolva.', {
            duration: 2000,
          });
        } else {
          // PIN kód kérése aktiváláshoz
          setPinInput('');
          setPinError('');
          setShowPinModal(true);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDeveloperMode]);

  // PIN modal megnyitásakor fókusz az input mezőre
  useEffect(() => {
    if (showPinModal && pinInputRef.current) {
      pinInputRef.current.focus();
    }
  }, [showPinModal]);

  const handlePinSubmit = () => {
    if (pinInput === '6969') {
      setIsDeveloperMode(true);
                toast('🔧 Fejlesztői mód aktiválva! Valós adatokkal fejlesztői számlák generálhatók.', {
        duration: 3000,
        icon: '⚠️',
      });
      setShowPinModal(false);
      setPinInput('');
      setPinError('');
    } else {
      setPinError('❌ Hibás PIN kód!');
      setPinInput(''); // Clear input on wrong PIN
    }
  };

  const loadInvoices = async () => {
    if (!user?.organizationId) {
      console.warn('No organization ID found for user');
      return;
    }

    try {
      setLoading(true);
      const invoiceList = await listInvoices(user.organizationId);
      setInvoices(invoiceList);
    } catch (error) {
      toast.error('Hiba a számlák betöltésekor');
      console.error('Error loading invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInvoiceSubmit = async (data: InvoiceRequest) => {
    if (!user?.organizationId) {
      toast.error('Nincs szervezet kiválasztva');
      return;
    }

    try {
      setLoading(true);
      
      if (editingInvoice) {
        // Szerkesztés
        // Remove the 'draft_' prefix to get the actual Firestore document ID
        const actualDraftId = editingInvoice.id.startsWith('draft_') ? editingInvoice.id.replace('draft_', '') : editingInvoice.id;
        await updateDraftInvoice(actualDraftId, data, user.organizationId);
        if (data.generateWarranty) {
          toast.success('Számla vázlat és garanciák sikeresen frissítve!');
        } else {
          toast.success('Számla vázlat sikeresen frissítve!');
        }
      } else {
        // Új vázlat
        await saveDraftInvoice(data, user.organizationId);
        if (data.generateWarranty) {
          toast.success('Számla vázlat sikeresen mentve és garanciák létrehozva!');
        } else {
          toast.success('Számla vázlat sikeresen mentve!');
        }
      }
      
      setShowInvoiceForm(false);
      setEditingInvoice(null);
      loadInvoices();
    } catch (error) {
      toast.error('Hiba a számla mentése során');
      console.error('Error saving invoice draft:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProformaSubmit = async (data: ProformaRequest) => {
    if (!user?.organizationId) {
      toast.error('Nincs szervezet kiválasztva');
      return;
    }

    try {
      setLoading(true);
      
      if (editingInvoice) {
        // Szerkesztés - proforma frissítése
        const actualDraftId = editingInvoice.id.startsWith('draft_') ? editingInvoice.id.replace('draft_', '') : editingInvoice.id;
        // Convert ProformaRequest to InvoiceRequest for updateDraftInvoice
        const invoiceData = {
          buyer: data.buyer,
          items: data.items,
          paymentMethod: data.paymentMethod,
          paymentDeadline: data.paymentDeadline,
          currency: data.currency,
          notes: data.notes,
          organizationId: data.organizationId,
          invoiceType: 'PROFORMA',
          validUntil: data.validUntil,
          convertToInvoice: data.convertToInvoice
        } as InvoiceRequest;
        await updateDraftInvoice(actualDraftId, invoiceData, user.organizationId);
        if (data.generateWarranty) {
          toast.success('Díjbekérő vázlat és garanciák sikeresen frissítve!');
        } else {
          toast.success('Díjbekérő vázlat sikeresen frissítve!');
        }
      } else {
        // Új vázlat
        await saveDraftProforma(data, user.organizationId);
        if (data.generateWarranty) {
          toast.success('Díjbekérő vázlat sikeresen mentve és garanciák létrehozva!');
        } else {
          toast.success('Díjbekérő vázlat sikeresen mentve!');
        }
      }
      
      setShowInvoiceForm(false);
      setEditingInvoice(null);
      loadInvoices();
    } catch (error) {
      toast.error('Hiba a díjbekérő mentése során');
      console.error('Error saving proforma draft:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateLive = async (invoiceId: string) => {
    setInvoiceToGenerate(invoiceId);
    setGenerateType('live'); // Default to live mode
    setShowGenerateModal(true);
  };

  const generateLiveDocument = async () => {
    if (!user?.organizationId || !invoiceToGenerate) {
      toast.error('Nincs szervezet kiválasztva');
      return;
    }

    try {
      setLoading(true);
      await generateLiveInvoice(invoiceToGenerate, user.organizationId, false); // false = éles mód
      toast.success('Éles számla sikeresen generálva!');
      loadInvoices();
    } catch (error) {
      toast.error(`Hiba az éles számla generálása során: ${error instanceof Error ? error.message : 'Ismeretlen hiba'}`);
      console.error('Error generating live invoice:', error);
    } finally {
      setLoading(false);
      setShowGenerateModal(false);
      setInvoiceToGenerate(null);
    }
  };

  const generateTestDocument = async () => {
    if (!user?.organizationId || !invoiceToGenerate) {
      toast.error('Nincs szervezet vagy számla kiválasztva');
      return;
    }

    try {
      setLoading(true);
      
      // Remove the 'draft_' prefix to get the actual Firestore document ID
      const actualDraftId = invoiceToGenerate.startsWith('draft_') ? invoiceToGenerate.replace('draft_', '') : invoiceToGenerate;
      
      // Generate test invoice with REAL data from the selected draft
      const testInvoiceData = await generateTestDocumentFromDraft(actualDraftId, user.organizationId);
      
      // Save to Firestore as dev invoice
      const devInvoiceDoc = {
        organizationId: user.organizationId,
        invoiceNumber: testInvoiceData.invoiceNumber,
        invoiceType: 'DEV_INVOICE',
        status: 'DEV',
        netAmount: testInvoiceData.netAmount,
        grossAmount: testInvoiceData.grossAmount,
        currency: testInvoiceData.currency,
        paymentMethod: testInvoiceData.paymentMethod,
        buyerName: testInvoiceData.buyer.name,
        buyerEmail: testInvoiceData.buyer.email || '',
        buyerTaxNumber: testInvoiceData.buyer.taxNumber || '',
        items: testInvoiceData.items,
        issueDate: new Date(),
        dueDate: testInvoiceData.dueDate,
        notes: testInvoiceData.notes,
        createdAt: new Date(),
        updatedAt: new Date(),
        isDeveloperMode: true
      };

      // Add to invoices collection
      await addDoc(collection(db, 'invoices'), devInvoiceDoc);
      
      // Generate offline test invoice (organizationData is included in testInvoiceData)
      await testInvoiceService.generateTestInvoice(testInvoiceData);
      
      toast.success('🔧 Fejlesztői számla sikeresen generálva és mentve!');
      
      // Refresh the invoice list
      loadInvoices();
      
    } catch (error) {
      toast.error(`Hiba a fejlesztői számla generálása során: ${error instanceof Error ? error.message : 'Ismeretlen hiba'}`);
      console.error('Error generating dev invoice:', error);
    } finally {
      setLoading(false);
      setShowGenerateModal(false);
      setInvoiceToGenerate(null);
    }
  };

  const handleGenerate = async () => {
    if (generateType === 'live') {
      await generateLiveDocument();
    } else {
      await generateTestDocument();
    }
  };

  const cancelGenerate = () => {
    setShowGenerateModal(false);
    setInvoiceToGenerate(null);
    setGenerateType('live'); // Reset to default
  };

  const handleEditDraft = async (invoiceId: string) => {
    if (!user?.organizationId) {
      toast.error('Nincs szervezet kiválasztva');
      return;
    }

    try {
      setLoading(true);
      // Remove the 'draft_' prefix to get the actual Firestore document ID
      const actualDraftId = invoiceId.startsWith('draft_') ? invoiceId.replace('draft_', '') : invoiceId;
      const invoice = await getDraftInvoice(actualDraftId, user.organizationId);
      setEditingInvoice(invoice);
      setFormType(invoice.invoiceType);
      setShowInvoiceForm(true);
    } catch (error) {
      toast.error('Hiba a vázlat betöltésekor');
      console.error('Error loading draft invoice:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDraft = async (invoiceId: string) => {
    setInvoiceToDelete(invoiceId);
    setShowDeleteModal(true);
  };

  const confirmDeleteDraft = async () => {
    if (!user?.organizationId || !invoiceToDelete) {
      toast.error('Nincs szervezet kiválasztva');
      return;
    }

    try {
      setLoading(true);
      // Remove the 'draft_' prefix to get the actual Firestore document ID
      const actualDraftId = invoiceToDelete.startsWith('draft_') ? invoiceToDelete.replace('draft_', '') : invoiceToDelete;
      await deleteDraftInvoice(actualDraftId, user.organizationId);
      toast.success('Vázlat sikeresen törölve!');
      loadInvoices();
    } catch (error) {
      toast.error('Hiba a vázlat törlése során');
      console.error('Error deleting draft invoice:', error);
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setInvoiceToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setInvoiceToDelete(null);
  };

  // PDF letöltése/megtekintése
  const handleViewPdf = async (invoice: InvoiceListItem) => {
    if (!user?.organizationId) return;

    // Vázlatokhoz nincs PDF
    if (invoice.status === 'DRAFT') {
      toast('A vázlatokhoz nincs PDF elérhető. Generálja le előbb a számlát!', {
        icon: 'ℹ️',
      });
      return;
    }

    // Fejlesztői számlák esetén újrageneráljuk a nyomtatási ablakot
    if (invoice.status === 'DEV' || invoice.type === 'DEV_INVOICE') {
      try {
        const loadingToast = toast.loading('Fejlesztői számla megnyitása...');
        
        // Get the dev invoice data from Firestore (not from draft)
        const actualInvoiceId = invoice.id.startsWith('live_') ? invoice.id.replace('live_', '') : invoice.id;
        
        // Get the dev invoice document directly
        const devInvoiceDoc = await getDoc(doc(db, 'invoices', actualInvoiceId));
        
        if (!devInvoiceDoc.exists()) {
          throw new Error('Fejlesztői számla nem található');
        }
        
        const devInvoiceData = devInvoiceDoc.data();
        
        // Get organization data from both organizations and billingSettings
        const [orgDoc, billingSettingsSnapshot] = await Promise.all([
          getDoc(doc(db, 'organizations', user.organizationId)),
          getDocs(query(
            collection(db, 'billingSettings'),
            where('organizationId', '==', user.organizationId),
            where('isActive', '==', true),
            limit(1)
          ))
        ]);
        
        const organizationInfo = orgDoc.exists() ? orgDoc.data() : null;
        const billingSettings = billingSettingsSnapshot.empty ? null : billingSettingsSnapshot.docs[0].data();
        
        // Transform saved dev invoice data to printable format
        const printableData = {
          invoiceNumber: devInvoiceData.invoiceNumber,
          invoiceType: devInvoiceData.invoiceType || 'INVOICE',
          issueDate: devInvoiceData.issueDate?.toDate?.() || new Date(devInvoiceData.issueDate),
          dueDate: devInvoiceData.dueDate?.toDate?.() || new Date(devInvoiceData.dueDate),
          paymentMethod: devInvoiceData.paymentMethod,
          paymentDeadline: devInvoiceData.paymentDeadline || 0,
          currency: devInvoiceData.currency,
          
          buyer: {
            name: devInvoiceData.buyerName,
            email: devInvoiceData.buyerEmail,
            taxNumber: devInvoiceData.buyerTaxNumber,
            // Reconstruct address if available
            address: devInvoiceData.buyerAddress ? {
              street: devInvoiceData.buyerAddress.split(',')[0]?.trim() || '',
              city: devInvoiceData.buyerAddress.split(',')[1]?.trim() || '',
              postalCode: devInvoiceData.buyerAddress.split(',')[2]?.trim() || '',
              country: 'Magyarország'
            } : undefined
          },
          
          items: devInvoiceData.items || [],
          notes: devInvoiceData.notes || 'Fejlesztői módban generált bizonylat.',
          
          organization: {
            name: organizationInfo?.name || billingSettings?.sellerData?.name || billingSettings?.companyName || 'Cégnév',
            taxNumber: organizationInfo?.taxNumber || billingSettings?.sellerData?.taxNumber || '00000000-0-00',
            address: organizationInfo?.address || billingSettings?.sellerData?.address || 'Cím utca 1.',
            phone: organizationInfo?.phone || billingSettings?.sellerData?.phone || '+36 1 000 0000',
            email: organizationInfo?.email || billingSettings?.sellerData?.email || 'info@cegnev.hu',
            bankAccount: billingSettings?.sellerData?.bankAccount || '12345678-12345678-12345678'
          }
        };
        
        console.log('Printable data for test invoice:', printableData);
        console.log('Organization data being passed:', printableData.organization);
        
        // Generate the printable version
        await testInvoiceService.generateTestInvoice(printableData, printableData.organization);
        
        toast.success('Fejlesztői számla megnyitva új ablakban', { id: loadingToast });
      } catch (error) {
        console.error('Error opening dev invoice:', error);
        toast.error('Hiba a fejlesztői számla megnyitásakor');
      }
      return;
    }

    const loadingToast = toast.loading('PDF betöltése...');
    
    try {
      // Get PDF data without downloading
      const pdfResponse = await billingService.downloadInvoicePdf(
        invoice.number, 
        user.organizationId,
        invoice.type as 'INVOICE' | 'PROFORMA' | 'CREDIT_NOTE'
      );
      
      if (pdfResponse.success && pdfResponse.pdfBase64) {
        // Convert base64 to blob
        const byteCharacters = atob(pdfResponse.pdfBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });

        // Open PDF in new window for viewing
        const url = window.URL.createObjectURL(blob);
        const newWindow = window.open(url, '_blank');
        
        if (!newWindow) {
          // If popup was blocked, show download option
          toast.error('Az ablak megnyitása blokkolva. PDF letöltése...', { id: loadingToast });
          const link = document.createElement('a');
          link.href = url;
          link.download = pdfResponse.fileName || `${invoice.number}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          toast.success('PDF megnyitva új ablakban', { id: loadingToast });
        }
        
        // Clean up URL after a delay
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      } else {
        throw new Error('PDF betöltése sikertelen');
      }
    } catch (error) {
      console.error('Error viewing PDF:', error);
      toast.error(
        error instanceof Error 
          ? `PDF megtekintési hiba: ${error.message}` 
          : 'Hiba történt a PDF megtekintésekor',
        { id: loadingToast }
      );
    }
  };

  // PDF letöltése (letöltés fájlként)
  const handleDownloadPdf = async (invoice: InvoiceListItem) => {
    if (!user?.organizationId) return;

    // Vázlatokhoz nincs PDF
    if (invoice.status === 'DRAFT') {
      toast('A vázlatokhoz nincs PDF elérhető. Generálja le előbb a számlát!', {
        icon: 'ℹ️',
      });
      return;
    }

    const loadingToast = toast.loading('PDF letöltése...');
    
    try {
      await billingService.downloadAndSavePdf(
        invoice.number, 
        user.organizationId,
        invoice.type as 'INVOICE' | 'PROFORMA' | 'CREDIT_NOTE'
      );
      
      toast.success('PDF letöltése megkezdődött', { id: loadingToast });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error(
        error instanceof Error 
          ? `PDF letöltési hiba: ${error.message}` 
          : 'Hiba történt a PDF letöltésekor',
        { id: loadingToast }
      );
    }
  };

  // Handle payment status update
  const handleMarkAsPaid = (invoice: InvoiceListItem) => {
    setInvoiceToUpdate(invoice);
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setShowPaymentModal(true);
  };

  const confirmMarkAsPaid = async () => {
    if (!invoiceToUpdate || !user?.organizationId) {
      toast.error('Nincs kiválasztott számla');
      return;
    }

    try {
      setLoading(true);
      await billingService.updateInvoiceStatus(
        invoiceToUpdate.id,
        user.organizationId,
        'PAID',
        new Date(paymentDate)
      );
      
      toast.success('Számla sikeresen fizetett státuszra állítva!');
      setShowPaymentModal(false);
      setInvoiceToUpdate(null);
      setPaymentDate('');
      loadInvoices();
    } catch (error) {
      toast.error(`Hiba a számla státusz frissítésekor: ${error instanceof Error ? error.message : 'Ismeretlen hiba'}`);
      console.error('Error updating payment status:', error);
    } finally {
      setLoading(false);
    }
  };

  const cancelPaymentUpdate = () => {
    setShowPaymentModal(false);
    setInvoiceToUpdate(null);
    setPaymentDate('');
  };

  // Handle credit note creation
  const handleCreateCreditNote = (invoice: InvoiceListItem) => {
    setInvoiceToCredit(invoice);
    setCreditNoteReason('Sztornó számla');
    setCreditNoteType('FULL');
    setShowCreditNoteModal(true);
  };

  const confirmCreateCreditNote = async () => {
    if (!invoiceToCredit || !user?.organizationId) {
      toast.error('Nincs kiválasztott számla');
      return;
    }

    try {
      setLoading(true);
      const creditNoteData = {
        originalInvoiceNumber: invoiceToCredit.number,
        organizationId: user.organizationId,
        reason: creditNoteReason,
        creditNoteType: creditNoteType,
        items: [] // We'll use the original invoice items from backend
      };

      await billingService.createCreditNote(creditNoteData, user.organizationId);
      
      toast.success('Sztornó számla sikeresen létrehozva!');
      setShowCreditNoteModal(false);
      setInvoiceToCredit(null);
      setCreditNoteReason('');
      setCreditNoteType('FULL');
      loadInvoices();
    } catch (error) {
      toast.error(`Hiba a sztornó számla létrehozásakor: ${error instanceof Error ? error.message : 'Ismeretlen hiba'}`);
      console.error('Error creating credit note:', error);
    } finally {
      setLoading(false);
    }
  };

  const cancelCreditNote = () => {
    setShowCreditNoteModal(false);
    setInvoiceToCredit(null);
    setCreditNoteReason('');
    setCreditNoteType('FULL');
  };

  const handleGenerateWarranty = (invoice: InvoiceListItem) => {
    setInvoiceToWarranty(invoice);
    setShowWarrantyModal(true);
  };

  const confirmGenerateWarranty = async () => {
    if (!invoiceToWarranty || !user?.organizationId) return;

    try {
      setLoading(true);
      
      // Import the warranty generation function
      const billingServiceModule = await import('../../lib/services/billingService');
      
      // Call a simplified warranty generation function that works with invoice ID
      await billingServiceModule.generateWarrantiesFromInvoiceId(invoiceToWarranty.id, user.organizationId);
      
      toast.success('Garanciák sikeresen generálva!');
      setShowWarrantyModal(false);
      setInvoiceToWarranty(null);
    } catch (error) {
      console.error('Error generating warranties:', error);
      toast.error('Hiba a garanciák generálásakor');
    } finally {
      setLoading(false);
    }
  };

  const cancelWarrantyGeneration = () => {
    setShowWarrantyModal(false);
    setInvoiceToWarranty(null);
  };

  const openInvoiceForm = (type: 'INVOICE' | 'PROFORMA') => {
    setFormType(type);
    setEditingInvoice(null); // Új számla esetén nincs szerkesztés
    setShowInvoiceForm(true);
  };

  const closeInvoiceForm = () => {
    setShowInvoiceForm(false);
    setEditingInvoice(null);
  };

  const getStatusBadge = (invoice: InvoiceListItem) => {
    if (invoice.status === 'DRAFT') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
          <DocumentIcon className="w-3 h-3 mr-1" />
          Vázlat
        </span>
      );
    }

    if (invoice.paid) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
          <CheckCircleIcon className="w-3 h-3 mr-1" />
          Fizetve
        </span>
      );
    }

    const dueDate = invoice.dueDate instanceof Date ? invoice.dueDate : new Date(invoice.dueDate);
    const now = new Date();
    const isOverdue = dueDate < now;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        isOverdue ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200' : 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
      }`}>
        <ClockIcon className="w-3 h-3 mr-1" />
        {isOverdue ? 'Lejárt' : 'Függőben'}
      </span>
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('hu-HU').format(Math.round(amount)) + ' Ft';
  };

  const formatDate = (date: string | Date) => {
    const dateObj = date instanceof Date ? date : new Date(date);
    return dateObj.toLocaleDateString('hu-HU');
  };

  // Szűrés és keresés
  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = invoice.buyerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         invoice.number.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;

    switch (filter) {
      case 'paid':
        return invoice.paid;
      case 'unpaid':
        return !invoice.paid && invoice.type === 'INVOICE' && invoice.status !== 'DRAFT';
      case 'overdue':
        return !invoice.paid && (invoice.dueDate instanceof Date ? invoice.dueDate : new Date(invoice.dueDate)) < new Date() && invoice.type === 'INVOICE' && invoice.status !== 'DRAFT';
      case 'draft':
        return invoice.status === 'DRAFT';
      case 'dev':
        return invoice.status === 'DEV' || invoice.type === 'DEV_INVOICE';
      default:
        // Ha nincs fejlesztői mód, ne mutassuk a DEV számlákat
        if (!isDeveloperMode && (invoice.status === 'DEV' || invoice.type === 'DEV_INVOICE')) {
          return false;
        }
        return true;
    }
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredInvoices.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentInvoices = filteredInvoices.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  return (
    <div className="space-y-4 sm:space-y-6 p-4">
      {isDeveloperMode && (
        <div className="flex justify-center">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            🔧 Fejlesztői mód aktív
          </span>
        </div>
      )}
      
      {/* Keresés és szűrők */}
      <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-600 p-3 sm:p-4">
        <div className="flex flex-col gap-3 sm:gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Keresés számlaszám vagy vevő neve alapján..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'all', label: 'Összes', count: invoices.filter(i => !isDeveloperMode ? (i.status !== 'DEV' && i.type !== 'DEV_INVOICE') : true).length },
              { key: 'draft', label: 'Vázlatok', count: invoices.filter(i => i.status === 'DRAFT').length },
              { key: 'paid', label: 'Fizetve', count: invoices.filter(i => i.paid).length },
              { key: 'unpaid', label: 'Függőben', count: invoices.filter(i => !i.paid && i.type === 'INVOICE' && i.status !== 'DRAFT').length },
              { key: 'overdue', label: 'Lejárt', count: invoices.filter(i => !i.paid && (i.dueDate instanceof Date ? i.dueDate : new Date(i.dueDate)) < new Date() && i.type === 'INVOICE' && i.status !== 'DRAFT').length },
              ...(isDeveloperMode ? [{ key: 'dev', label: '🔧 Fejlesztői', count: invoices.filter(i => i.status === 'DEV' || i.type === 'DEV_INVOICE').length }] : [])
            ].map(({ key, label, count }) => (
              <button
                key={key}
                onClick={() => setFilter(key as any)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === key
                    ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 border border-primary-300 dark:border-primary-700'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {label} ({count})
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Számlák listája */}
      <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-600 p-3 sm:p-4">
        <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-3 sm:mb-4">
          Számlák és díjbekérők ({filteredInvoices.length})
        </h3>
        
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : filteredInvoices.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-2" />
            <p>
              {searchTerm || filter !== 'all' 
                ? 'Nincs találat a keresési feltételeknek megfelelően' 
                : 'Még nincsenek számlák'
              }
            </p>
            {!searchTerm && filter === 'all' && (
              <p className="text-sm mt-1">Kezdje el az első számla létrehozásával</p>
            )}
          </div>
        ) : (
          <>
            {/* Desktop táblázat */}
            <div className="hidden md:block overflow-x-auto w-full">
              <table className="min-w-max w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th scope="col" className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[100px]">
                      Számlaszám
                    </th>
                    <th scope="col" className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[80px] hidden lg:table-cell">
                      Típus
                    </th>
                    <th scope="col" className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[150px]">
                      Vevő
                    </th>
                    <th scope="col" className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[100px]">
                      Összeg
                    </th>
                    <th scope="col" className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[80px] hidden lg:table-cell">
                      Dátum
                    </th>
                    <th scope="col" className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[80px] hidden xl:table-cell">
                      Határidő
                    </th>
                    <th scope="col" className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[80px]">
                      Státusz
                    </th>
                    <th scope="col" className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[100px]">
                      Műveletek
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {currentInvoices.map((invoice) => (
                    <tr key={invoice.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-2 py-1.5 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                        <div>{invoice.number}</div>
                        {/* Típus megjelenítése lg alatt kis badge-ként */}
                        <div className="lg:hidden mt-1">
                          {invoice.type === 'PROFORMA' ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                              <ClockIcon className="w-2 h-2 mr-1" />
                              Díjbekérő
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                              <DocumentTextIcon className="w-2 h-2 mr-1" />
                              Számla
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap hidden lg:table-cell">
                        {invoice.type === 'PROFORMA' ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                            <ClockIcon className="w-3 h-3 mr-1" />
                            Díjbekérő
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                            <DocumentTextIcon className="w-3 h-3 mr-1" />
                            Számla
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-sm">
                        <div className="text-gray-900 dark:text-gray-100">{invoice.buyerName}</div>
                        {invoice.buyerEmail && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">{invoice.buyerEmail}</div>
                        )}
                        {/* Dátum megjelenítése lg alatt */}
                        <div className="lg:hidden text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {formatDate(invoice.issueDate)}
                        </div>
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100 text-right">
                        {formatCurrency(invoice.totalAmount)}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 hidden lg:table-cell">
                        <div className="flex items-center">
                          <CalendarIcon className="h-3 w-3 mr-1 text-gray-400 dark:text-gray-500" />
                          {formatDate(invoice.issueDate)}
                        </div>
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 hidden xl:table-cell">
                        <div className="flex items-center">
                          <CalendarIcon className="h-3 w-3 mr-1 text-gray-400 dark:text-gray-500" />
                          {formatDate(invoice.dueDate)}
                        </div>
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-center">
                        {getStatusBadge(invoice)}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center space-x-1">
                          {invoice.status === 'DRAFT' && (
                            <>
                              <button
                                onClick={() => handleEditDraft(invoice.id)}
                                disabled={loading}
                                className="text-blue-600 hover:text-blue-500 p-1 rounded-full hover:bg-blue-50 disabled:opacity-50"
                                title="Vázlat szerkesztése"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteDraft(invoice.id)}
                                disabled={loading}
                                className="text-red-600 hover:text-red-500 p-1 rounded-full hover:bg-red-50 disabled:opacity-50"
                                title="Vázlat törlése"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleGenerateLive(invoice.id)}
                                disabled={loading}
                                className="text-green-600 hover:text-green-500 p-1 rounded-full hover:bg-green-50 disabled:opacity-50"
                                title="Éles számla generálás"
                              >
                                <CogIcon className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          {invoice.status !== 'DRAFT' && (
                            <>
                              <button
                                onClick={() => handleViewPdf(invoice)}
                                disabled={loading}
                                className="text-primary-600 hover:text-primary-500 p-1 rounded-full hover:bg-primary-50 disabled:opacity-50"
                                title="PDF megtekintése"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </button>
                              {invoice.status !== 'DEV' && invoice.type !== 'DEV_INVOICE' && (
                                <button
                                  onClick={() => handleDownloadPdf(invoice)}
                                  disabled={loading}
                                  className="text-green-600 hover:text-green-500 p-1 rounded-full hover:bg-green-50 disabled:opacity-50"
                                  title="PDF letöltése"
                                >
                                  <ArrowDownTrayIcon className="h-4 w-4" />
                                </button>
                              )}
                              {!invoice.paid && invoice.type === 'INVOICE' && invoice.status !== 'CANCELLED' && (
                                <button
                                  onClick={() => handleMarkAsPaid(invoice)}
                                  disabled={loading}
                                  className="text-blue-600 hover:text-blue-500 p-1 rounded-full hover:bg-blue-50 disabled:opacity-50"
                                  title="Fizetett-re állítás"
                                >
                                  <CurrencyDollarIcon className="h-4 w-4" />
                                </button>
                              )}
                              {invoice.type === 'INVOICE' && invoice.status !== 'CANCELLED' && (
                                <button
                                  onClick={() => handleCreateCreditNote(invoice)}
                                  disabled={loading}
                                  className="text-red-600 hover:text-red-500 p-1 rounded-full hover:bg-red-50 disabled:opacity-50"
                                  title="Sztornó létrehozása"
                                >
                                  <XCircleIcon className="h-4 w-4" />
                                </button>
                              )}
                              {/* Garancia generálás gomb - minden éles számlánál */}
                              <button
                                onClick={() => handleGenerateWarranty(invoice)}
                                disabled={loading}
                                className="text-green-600 hover:text-green-500 p-1 rounded-full hover:bg-green-50 disabled:opacity-50"
                                title="Garancia generálás"
                              >
                                <ShieldCheckIcon className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          {invoice.status === 'DRAFT' && (
                            <button
                              onClick={() => {
                                toast('A vázlatokhoz nincs PDF elérhető. Generálja le előbb a számlát!', {
                                  icon: 'ℹ️',
                                });
                              }}
                              disabled={true}
                              className="text-gray-400 p-1 rounded-full opacity-50 cursor-not-allowed"
                              title="Vázlathoz nincs PDF"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobil kártyák */}
            <div className="md:hidden space-y-3 -mx-3 sm:-mx-4">
              {currentInvoices.map((invoice) => (
                <div key={invoice.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-600 shadow-sm overflow-hidden mx-3 sm:mx-4">
                  {/* Kártya fejléc */}
                  <div className="p-3 sm:p-4 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-base font-semibold text-gray-900 dark:text-gray-100">
                        {invoice.number}
                      </div>
                      {invoice.type === 'PROFORMA' ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                          <ClockIcon className="w-3 h-3 mr-1" />
                          Díjbekérő
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                          <DocumentTextIcon className="w-3 h-3 mr-1" />
                          Számla
                        </span>
                      )}
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Vevő:</span>
                        <div className="text-sm text-gray-900 dark:text-gray-100 text-right">
                          <div>{invoice.buyerName}</div>
                          {invoice.buyerEmail && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">{invoice.buyerEmail}</div>
                          )}
                        </div>
                      </div>

                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Összeg:</span>
                        <span className="text-base font-semibold text-gray-900 dark:text-gray-100">
                          {formatCurrency(invoice.totalAmount)}
                        </span>
                      </div>

                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Dátum:</span>
                        <span className="text-sm text-gray-900 dark:text-gray-100">
                          {formatDate(invoice.issueDate)}
                        </span>
                      </div>

                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Határidő:</span>
                        <span className="text-sm text-gray-900 dark:text-gray-100">
                          {formatDate(invoice.dueDate)}
                        </span>
                      </div>

                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Státusz:</span>
                        {getStatusBadge(invoice)}
                      </div>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="p-3 sm:p-4 bg-gray-50 dark:bg-gray-700">
                    <div className="flex justify-center flex-wrap gap-2">
                      {invoice.status === 'DRAFT' && (
                        <>
                          <button
                            onClick={() => handleEditDraft(invoice.id)}
                            disabled={loading}
                            className="flex items-center px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-blue-600 bg-blue-50 dark:bg-blue-900 dark:text-blue-200 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-800 disabled:opacity-50"
                          >
                            <PencilIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                            <span className="hidden sm:inline">Szerkesztés</span>
                            <span className="sm:hidden">Szerk.</span>
                          </button>
                          <button
                            onClick={() => handleDeleteDraft(invoice.id)}
                            disabled={loading}
                            className="flex items-center px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-red-600 bg-red-50 dark:bg-red-900 dark:text-red-200 rounded-lg hover:bg-red-100 dark:hover:bg-red-800 disabled:opacity-50"
                          >
                            <TrashIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                            Törlés
                          </button>
                          <button
                            onClick={() => handleGenerateLive(invoice.id)}
                            disabled={loading}
                            className="flex items-center px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-green-600 bg-green-50 dark:bg-green-900 dark:text-green-200 rounded-lg hover:bg-green-100 dark:hover:bg-green-800 disabled:opacity-50"
                          >
                            <CogIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                            <span className="hidden sm:inline">Generálás</span>
                            <span className="sm:hidden">Gen.</span>
                          </button>
                        </>
                                              )}
                      {invoice.status !== 'DRAFT' && (
                        <>
                          <button
                            onClick={() => handleViewPdf(invoice)}
                            disabled={loading}
                            className="flex items-center px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-primary-600 bg-primary-50 dark:bg-primary-900 dark:text-primary-200 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-800 disabled:opacity-50"
                            title="PDF megtekintése"
                          >
                            <EyeIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                            <span className="hidden sm:inline">Megtekintés</span>
                            <span className="sm:hidden">Nézet</span>
                          </button>
                          {invoice.status !== 'DEV' && invoice.type !== 'DEV_INVOICE' && (
                            <button
                              onClick={() => handleDownloadPdf(invoice)}
                              disabled={loading}
                              className="flex items-center px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-green-600 bg-green-50 dark:bg-green-900 dark:text-green-200 rounded-lg hover:bg-green-100 dark:hover:bg-green-800 disabled:opacity-50"
                              title="PDF letöltése"
                            >
                              <ArrowDownTrayIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                              <span className="hidden sm:inline">Letöltés</span>
                              <span className="sm:hidden">PDF</span>
                            </button>
                          )}
                          {!invoice.paid && invoice.type === 'INVOICE' && invoice.status !== 'CANCELLED' && (
                            <button
                              onClick={() => handleMarkAsPaid(invoice)}
                              disabled={loading}
                              className="flex items-center px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-blue-600 bg-blue-50 dark:bg-blue-900 dark:text-blue-200 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-800 disabled:opacity-50"
                              title="Fizetett-re állítás"
                            >
                              <CurrencyDollarIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                              <span className="hidden sm:inline">Fizetett</span>
                              <span className="sm:hidden">Fizetve</span>
                            </button>
                          )}
                          {invoice.type === 'INVOICE' && invoice.status !== 'CANCELLED' && (
                            <button
                              onClick={() => handleCreateCreditNote(invoice)}
                              disabled={loading}
                              className="flex items-center px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-red-600 bg-red-50 dark:bg-red-900 dark:text-red-200 rounded-lg hover:bg-red-100 dark:hover:bg-red-800 disabled:opacity-50"
                              title="Sztornó létrehozása"
                            >
                              <XCircleIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                              <span className="hidden sm:inline">Sztornó</span>
                              <span className="sm:hidden">Sztr.</span>
                            </button>
                          )}
                          
                          {/* Garancia generálás - minden éles számlánál elérhető */}
                          <button
                            onClick={() => handleGenerateWarranty(invoice)}
                            disabled={loading}
                            className="flex items-center px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-green-600 bg-green-50 dark:bg-green-900 dark:text-green-200 rounded-lg hover:bg-green-100 dark:hover:bg-green-800 disabled:opacity-50"
                            title="Garancia generálás"
                          >
                            <ShieldCheckIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                            <span className="hidden sm:inline">Garancia</span>
                            <span className="sm:hidden">Gar.</span>
                          </button>
                        </>
                      )}
                      {invoice.status === 'DRAFT' && (
                        <button
                          disabled={true}
                          className="flex items-center px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-gray-400 bg-gray-100 dark:bg-gray-700 dark:text-gray-500 rounded-lg opacity-50 cursor-not-allowed"
                          title="Vázlathoz nincs PDF"
                        >
                          <EyeIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                          <span className="hidden sm:inline">Nincs PDF</span>
                          <span className="sm:hidden">N/A</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {filteredInvoices.length > 0 && (
              <div className="relative z-10 mt-6">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  itemsPerPage={itemsPerPage}
                  totalItems={filteredInvoices.length}
                  indexOfFirstItem={indexOfFirstItem}
                  indexOfLastItem={indexOfLastItem}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Számla/díjbekérő form modal */}
      {showInvoiceForm && (
        <InvoiceForm
          documentType={formType}
          onSubmit={formType === 'INVOICE' ? handleInvoiceSubmit : handleProformaSubmit}
          onClose={closeInvoiceForm}
          isOpen={showInvoiceForm}
          loading={loading}
          initialData={editingInvoice}
        />
      )}

      {/* Confirm Delete Modal */}
      {showDeleteModal && (
        <ConfirmDeleteModal
          isOpen={showDeleteModal}
          onConfirm={confirmDeleteDraft}
          onClose={cancelDelete}
          title="Vázlat törlése"
          deleteMessage="Biztosan törölni szeretné ezt a vázlatot?"
          deleteWarning="Ez a művelet nem vonható vissza."
        />
      )}

      {/* Generate Invoice Modal */}
      {showGenerateModal && (
        <Modal
          isOpen={showGenerateModal}
          onClose={cancelGenerate}
          title={`Bizonylat generálás ${isDeveloperMode ? '🔧' : ''}`}
          size="md"
        >
          <div className="p-6">
            {isDeveloperMode && (
              <div className="bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3 mb-4">
                <div className="flex items-center">
                  <span className="text-yellow-800 dark:text-yellow-200 text-sm font-medium">
                    ⚠️ Fejlesztői mód aktív - Valós adatokkal fejlesztői számlák generálhatók
                  </span>
                </div>
              </div>
            )}
            
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              {isDeveloperMode 
                ? 'Válassza ki a bizonylat típusát:'
                : 'Biztosan generálja az éles bizonylatot?'
              }
            </p>
            
            <div className="space-y-4 mb-6">
              {isDeveloperMode && (
                <label className="flex items-center space-x-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                  <input
                    type="radio"
                    name="generateType"
                    value="dev"
                    checked={generateType === 'dev'}
                    onChange={(e) => setGenerateType(e.target.value as 'live' | 'dev')}
                    className="w-4 h-4 text-blue-600"
                  />
                  <div className="flex items-center flex-1">
                    <CogIcon className="h-5 w-5 mr-3 text-blue-600" />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-gray-100">Fejlesztői bizonylat</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">Valós adatokkal, saját sorszámmal, nyomtatható</div>
                    </div>
                  </div>
                </label>
              )}
              
              <label className="flex items-center space-x-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                <input
                  type="radio"
                  name="generateType"
                  value="live"
                  checked={generateType === 'live'}
                  onChange={(e) => setGenerateType(e.target.value as 'live' | 'dev')}
                  className="w-4 h-4 text-red-600"
                />
                <div className="flex items-center flex-1">
                  <DocumentTextIcon className="h-5 w-5 mr-3 text-red-600" />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">Éles bizonylat</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Szamlazz.hu rendszerbe küldés (PDF)</div>
                  </div>
                </div>
              </label>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={cancelGenerate}
                disabled={loading}
                className="flex-1 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors disabled:opacity-50"
              >
                Mégse
              </button>
              <button
                onClick={handleGenerate}
                disabled={loading}
                className={`flex-1 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
                  generateType === 'live' 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Generálás...
                  </div>
                ) : (
                  `${generateType === 'live' ? 'Éles' : 'Fejlesztői'} bizonylat generálása`
                )}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Payment Status Update Modal */}
      {showPaymentModal && (
        <Modal
          isOpen={showPaymentModal}
          onClose={cancelPaymentUpdate}
          title="Számla fizetett-re állítása"
          size="md"
        >
          <div className="p-6">
            <div className="mb-4">
              <p className="text-gray-600 dark:text-gray-300 mb-2">
                Biztosan fizetett státuszra szeretné állítani ezt a számlát?
              </p>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 mb-4">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  <div><strong>Számlaszám:</strong> {invoiceToUpdate?.number}</div>
                  <div><strong>Vevő:</strong> {invoiceToUpdate?.buyerName}</div>
                  <div><strong>Összeg:</strong> {invoiceToUpdate ? formatCurrency(invoiceToUpdate.totalAmount) : ''}</div>
                </div>
              </div>
            </div>
            
            <div className="mb-6">
              <label htmlFor="paymentDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Fizetés dátuma
              </label>
              <input
                type="date"
                id="paymentDate"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={cancelPaymentUpdate}
                disabled={loading}
                className="flex-1 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors disabled:opacity-50"
              >
                Mégse
              </button>
              <button
                onClick={confirmMarkAsPaid}
                disabled={loading}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Frissítés...
                  </div>
                ) : (
                  'Fizetett-re állítás'
                )}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Credit Note Creation Modal */}
      {showCreditNoteModal && (
        <Modal
          isOpen={showCreditNoteModal}
          onClose={cancelCreditNote}
          title="Sztornó számla létrehozása"
          size="md"
        >
          <div className="p-6">
            <div className="mb-4">
              <p className="text-gray-600 dark:text-gray-300 mb-2">
                Biztosan létrehoz egy sztornó számlát ehhez a számlához?
              </p>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 mb-4">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  <div><strong>Eredeti számlaszám:</strong> {invoiceToCredit?.number}</div>
                  <div><strong>Vevő:</strong> {invoiceToCredit?.buyerName}</div>
                  <div><strong>Összeg:</strong> {invoiceToCredit ? formatCurrency(invoiceToCredit.totalAmount) : ''}</div>
                </div>
              </div>
            </div>
            
            <div className="mb-4">
              <label htmlFor="creditNoteType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Sztornó típusa
              </label>
              <select
                id="creditNoteType"
                value={creditNoteType}
                onChange={(e) => setCreditNoteType(e.target.value as 'FULL' | 'PARTIAL')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="FULL">Teljes sztornó</option>
                <option value="PARTIAL">Részleges sztornó</option>
              </select>
            </div>
            
            <div className="mb-6">
              <label htmlFor="creditNoteReason" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Sztornó oka
              </label>
              <textarea
                id="creditNoteReason"
                value={creditNoteReason}
                onChange={(e) => setCreditNoteReason(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="Adja meg a sztornó okát..."
              />
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={cancelCreditNote}
                disabled={loading}
                className="flex-1 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors disabled:opacity-50"
              >
                Mégse
              </button>
              <button
                onClick={confirmCreateCreditNote}
                disabled={loading}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Létrehozás...
                  </div>
                ) : (
                  'Sztornó létrehozása'
                )}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Warranty Generation Modal */}
      {showWarrantyModal && (
        <Modal
          isOpen={showWarrantyModal}
          onClose={cancelWarrantyGeneration}
          title="Garancia generálás"
          size="md"
        >
          <div className="p-6">
            <div className="mb-4">
              <p className="text-gray-600 dark:text-gray-300 mb-2">
                Biztosan generál garanciákat ehhez a számlához?
              </p>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 mb-4">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  <div><strong>Számlaszám:</strong> {invoiceToWarranty?.number}</div>
                  <div><strong>Vevő:</strong> {invoiceToWarranty?.buyerName}</div>
                  <div><strong>Összeg:</strong> {invoiceToWarranty ? formatCurrency(invoiceToWarranty.totalAmount) : ''}</div>
                  <div><strong>Dátum:</strong> {invoiceToWarranty ? formatDate(invoiceToWarranty.issueDate) : ''}</div>
                </div>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900 rounded-lg p-3">
                <div className="flex items-start">
                  <ShieldCheckIcon className="h-5 w-5 text-blue-600 dark:text-blue-300 mt-0.5 mr-2 flex-shrink-0" />
                  <div className="text-sm text-blue-800 dark:text-blue-200">
                    <p className="font-medium mb-1">A rendszer automatikusan létrehozza a garanciákat:</p>
                    <ul className="list-disc list-inside space-y-1 text-xs">
                      <li>12 hónapos eladói garancia minden termékre</li>
                      <li>Vásárlási ár és dátum a számla alapján</li>
                      <li>Automatikus termék és ügyfél párosítás</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={cancelWarrantyGeneration}
                disabled={loading}
                className="flex-1 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors disabled:opacity-50"
              >
                Mégse
              </button>
              <button
                onClick={confirmGenerateWarranty}
                disabled={loading}
                className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Generálás...
                  </div>
                ) : (
                  'Garanciák generálása'
                )}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* PIN Modal */}
      {showPinModal && (
        <Modal
          isOpen={showPinModal}
          onClose={() => {
            setShowPinModal(false);
            setPinInput('');
            setPinError('');
          }}
          title=""
          size="md"
        >
          <div className="p-6">
            <div className="text-center mb-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900 mb-4">
                <LockClosedIcon className="h-6 w-6 text-blue-600 dark:text-blue-300" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Fejlesztői mód aktiválása
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Adja meg a PIN kódot a fejlesztői funkciók eléréséhez
              </p>
            </div>

            <div className="mb-4">
              <input
                ref={pinInputRef}
                type="password"
                placeholder="••••"
                value={pinInput}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, ''); // Csak számokat enged
                  if (value.length <= 4) {
                    setPinInput(value);
                    setPinError('');
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handlePinSubmit();
                  }
                }}
                className="w-full px-4 py-3 text-center text-2xl font-mono tracking-wider border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                maxLength={4}
                pattern="[0-9]*"
                inputMode="numeric"
                autoComplete="off"
              />
              
              {pinError && (
                <div className="mt-2 text-sm text-red-600 text-center">
                  {pinError}
                </div>
              )}
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowPinModal(false);
                  setPinInput('');
                  setPinError('');
                }}
                className="flex-1 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
              >
                Mégse
              </button>
              <button
                onClick={handlePinSubmit}
                disabled={!pinInput.trim()}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Aktiválás
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default InvoiceManager; 